import React, { FC } from 'react'
import {
  Dialog,
  DialogContent,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { NOTION_DIALOG_PAPER_SX } from '@/shared/styles/dialogStyles'
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { BodyText } from '@/components/atoms/BodyText'
import { MetaText } from '@/components/atoms/MetaText'
import { DialogHeader } from '@/components/atoms/DialogHeader'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale/ja'
import { useKasanHistoryQuery } from '@/apps/latest/features/guidance/hooks/queries/useKasanHistoryQuery'
import {
  KasanEvent,
  KasanPrescription,
} from '@/apps/latest/features/guidance/types/kasanHistory'

interface KasanHistoryModalProps {
  open: boolean
  onClose: () => void
  yakurekiId: string
  patientName?: string
  onSoapClick?: (eventId: string) => void
}

const formatDate = (dateString: string) => {
  return format(new Date(dateString), 'yyyy年M月d日 HH:mm', { locale: ja })
}

const PrescriptionItem: FC<{ prescription: KasanPrescription }> = ({
  prescription,
}) => (
  <BodyText sx={{ pl: 1, py: 0.25 }}>
    {prescription.drug_name} {prescription.dosage}
    {prescription.dosage_unit} ・ {prescription.usage} ・ {prescription.days}
    日分
  </BodyText>
)

const EventItem: FC<{
  event: KasanEvent
  onSoapClick?: (eventId: string) => void
}> = ({ event, onSoapClick }) => (
  <Box sx={{ py: 1.5 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        mb: 0.5,
      }}
    >
      <MetaText>{formatDate(event.created_at)}</MetaText>
      {event.soap_exists && onSoapClick && (
        <Button
          size="small"
          variant="text"
          onClick={() => onSoapClick(event.event_id)}
          // accordion 内 inline link としては body より弱い CTA で 12px 維持. NOTION_BUTTON_TEXT_SX は
          // DialogActions の余白前提なのでこの inline 用には不適.
          sx={{
            minWidth: 0,
            px: 1,
            py: 0.25,
            fontSize: 12,
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          SOAP を表示
        </Button>
      )}
    </Box>
    <Stack spacing={0}>
      {event.prescriptions.map((prescription, index) => (
        <PrescriptionItem key={index} prescription={prescription} />
      ))}
    </Stack>
  </Box>
)

export const KasanHistoryModal: FC<KasanHistoryModalProps> = ({
  open,
  onClose,
  yakurekiId,
  patientName: _patientName,
  onSoapClick,
}) => {
  const {
    data,
    isLoading: loading,
    error,
  } = useKasanHistoryQuery({
    yakurekiId,
    enabled: open,
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': NOTION_DIALOG_PAPER_SX,
      }}
    >
      <DialogHeader title="加算履歴" onClose={onClose} />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 160,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            加算履歴の取得に失敗しました。再度お試しください。
          </Alert>
        )}

        {data && data.kasan_history.length === 0 && (
          <MetaText sx={{ py: 4, textAlign: 'center', color: 'text.disabled' }}>
            加算履歴はありません
          </MetaText>
        )}

        {data && data.kasan_history.length > 0 && (
          <Stack spacing={0} sx={{ mt: 1 }}>
            {data.kasan_history.map((kasanItem, index) => (
              <Accordion
                key={index}
                disableGutters
                elevation={0}
                square
                sx={{
                  bgcolor: 'transparent',
                  borderTop:
                    index === 0
                      ? 'none'
                      : `1px solid ${colorTokens.border.default}`,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize="small" />}
                  sx={{
                    px: 0,
                    minHeight: 40,
                    '& .MuiAccordionSummary-content': { my: 1 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1.5,
                      width: '100%',
                    }}
                  >
                    <BodyText sx={{ fontWeight: 500 }}>
                      {kasanItem.kasan_name}
                    </BodyText>
                    <MetaText>{kasanItem.events.length}件</MetaText>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 1 }}>
                  {kasanItem.events.map((event, eventIndex) => (
                    <EventItem
                      key={eventIndex}
                      event={event}
                      onSoapClick={onSoapClick}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}
